/* A program page built from the owner's master specification
   ----------------------------------------------------------------------
   /programs/<slug> for every program in src/data/programSpecs.js
   (AI-Powered Business, Finance & Accounting, 7 October 2026). Same
   design as the other program pages (ProgramDetail.css, `prog-rich`).

   Sections, numbered in order of appearance: hero → trust strip → Why
   it matters (with sourced statistics where the spec has them) → the
   method (navy: how AI is used / how the course works, with its cards)
   → the 8 classes (class rhythm and the four phases) → what students
   learn and leave with → why Kidsology → questions → the other
   programs → call to action. The site's existing calls to action only:
   no new form, no price (spec 2.11 / 2.10). */

import { srcSetFor, SIZES } from "../../lib/img";
import React, { useEffect, useRef } from "react";
import ProgramFaq from "./ProgramFaq";
import { useLocale, useL } from "../../i18n/LocaleContext";
import SEO from "../../components/SEO";
import SiteHeader from "../../components/SiteHeader";
import LocaleLink from "../../components/LocaleLink";
import { PROGRAM_SPECS } from "../../data/programSpecs";
import { PROGRAM_PAGE_UI } from "../../data/programPages";
import { PROGRAMS } from "../../data/offer";
import { programCourseSchema, faqPageSchema, breadcrumbSchema } from "../../data/schemas";
import { ARROW } from "../../components/catalog/CatalogParts";
import { track } from "../../lib/analytics";
import "../Home.css";
import "../Programs.css";
import "./ProgramDetail.css";

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII"];

const CHECK = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const ICONS = [
  <svg key="a" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
  <svg key="b" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15.5 14" /></svg>,
  <svg key="c" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>,
  <svg key="d" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="9" r="6" /><polyline points="8 14 6 22 12 19 18 22 16 14" /></svg>,
];

export default function ProgramSpecPage({ slug }) {
  const { lp, locale } = useLocale();
  const raw = PROGRAM_SPECS[slug];
  const p = useL(raw);
  const ui = useL(PROGRAM_PAGE_UI);
  const others = useL(PROGRAMS.filter((o) => o.page !== raw.path));
  const rootRef = useRef(null);

  /* Reveal on scroll, as on the other program pages. */
  useEffect(() => {
    const els = rootRef.current ? Array.from(rootRef.current.querySelectorAll(".reveal")) : [];
    if (!("IntersectionObserver" in window) || els.length === 0) {
      els.forEach((el) => el.classList.add("in"));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); }
      }),
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [slug]);

  let n = 0;
  const label = (text, light = false) => (
    <span className={`el-label${light ? " el-label--light" : ""} reveal`}>{ROMAN[n++]} &nbsp;·&nbsp; {text}</span>
  );
  const twoLines = (lines) => lines.map((l, i) => <React.Fragment key={i}>{i > 0 && <br />}{l}</React.Fragment>);

  return (
    <div className="prog-page prog-rich" ref={rootRef}>
      <SEO
        title={p.seo.title}
        description={p.seo.description}
        path={raw.path}
        image={`${raw.photo.src}.jpg`}
        schema={[
          programCourseSchema(raw, locale),
          faqPageSchema(raw.faq.items, locale),
          breadcrumbSchema([
            { name: PROGRAM_PAGE_UI.home, path: "/" },
            { name: PROGRAM_PAGE_UI.programs, path: "/programs" },
            { name: raw.name, path: raw.path },
          ], locale),
        ]}
      />
      <SiteHeader />
      <main id="main">

      {/* ── Hero ── */}
      <header className="el-hero">
        <div className="el-hero-left">
          <span className="el-hero-tag">{p.tag}</span>
          <h1 className="el-hero-title el-display">{p.title.before}<em>{p.title.em}</em></h1>
          <p className="el-hero-headline">{p.headline}</p>
          <p className="el-hero-body">{p.body}</p>
          <div className="el-not-badges">
            {p.badges.map((b, i) => <span key={i} className="el-not-badge">{b}</span>)}
          </div>
          <div className="el-hero-actions">
            <a href={lp("/#contact")} className="btn btn-gold-lg" onClick={() => track("program_register_click", { program: slug })}>{ui.register}</a>
            <LocaleLink to="/schools#future-skills" className="el-text-link" onClick={() => track("program_schools_click", { program: slug })}>
              <span>{ui.forSchools}</span>{ARROW}
            </LocaleLink>
          </div>
        </div>
        <div className="el-hero-right">
          <picture>
            <source srcSet={srcSetFor(p.photo.src)} sizes={SIZES.half} type="image/webp" />
            <img src={`${p.photo.src}.jpg`} alt={p.photo.alt} className="el-hero-photo" />
          </picture>
          <div className="el-hero-photo-wash" />
          <div className="el-hero-bracket" />
        </div>
      </header>

      <div className="el-trust">
        {p.trust.map((item, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="el-trust-sep" />}
            <div className="el-trust-item">{ICONS[i % ICONS.length]} {item}</div>
          </React.Fragment>
        ))}
      </div>

      <div className="prog-content">

        {/* ── Why it matters ── */}
        <section className="el-section" aria-labelledby="why-title">
          <div className="el-inner">
            {label(p.why.label)}
            <h2 id="why-title" className="el-h2 el-display reveal" style={{ "--d": "60ms" }}>{twoLines(p.why.title)}</h2>
            <p className="el-lead reveal" style={{ "--d": "120ms" }}>{p.why.lead}</p>
            {p.why.stats && (
              <ul className="el-stats">
                {p.why.stats.map((s, i) => (
                  <li key={i} className="el-stat reveal" style={{ "--d": `${i * 90}ms` }}>
                    <p className="el-stat-fig" dir="ltr">{s.figure}</p>
                    <p className="el-stat-text">{s.text}</p>
                    <p className="el-stat-src">
                      {p.why.sourceLabel}:{" "}
                      <a href={s.url} target="_blank" rel="noopener noreferrer"><bdi>{s.source}</bdi></a>
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* ── The method (navy) ── */}
        <section className="el-section el-plan" aria-labelledby="method-title">
          <div className="el-inner">
            {label(p.method.label, true)}
            <h2 id="method-title" className="el-h2 el-h2--light el-display reveal" style={{ "--d": "60ms" }}>{twoLines(p.method.title)}</h2>
            <p className="el-lead el-lead--light reveal" style={{ "--d": "120ms" }}>{p.method.lead}</p>
            <p className="el-subhead el-subhead--light reveal">{p.method.cardsTitle}</p>
            <div className="el-session-cards">
              {(p.method.cards || p.method.rules.map((r) => ({ t: r }))).map((c, i) => (
                <div key={i} className="el-scard reveal" style={{ "--d": `${i * 90}ms` }}>
                  <div className="el-scard-icon el-scard-num" aria-hidden="true">{i + 1}</div>
                  <p className="el-scard-title">{c.t}</p>
                  {c.b && <p className="el-scard-desc">{c.b}</p>}
                </div>
              ))}
            </div>
            {p.method.note && <p className="el-lead el-lead--light el-method-note reveal">{p.method.note}</p>}
          </div>
        </section>

        {/* ── The 8 classes ── */}
        <section className="el-section" aria-labelledby="classes-title">
          <div className="el-inner">
            {label(p.classes.label)}
            <h2 id="classes-title" className="el-h2 el-display reveal" style={{ "--d": "60ms" }}>{twoLines(p.classes.title)}</h2>
            <p className="el-lead reveal" style={{ "--d": "120ms" }}>{p.classes.lead}</p>
            <div className="el-rhythm reveal">
              <p className="el-subhead">{p.classes.rhythmTitle}</p>
              <ol className="el-rhythm-list">
                {p.classes.rhythm.map((r, i) => (
                  <li key={i} style={{ flexGrow: Number(r.v) }}>
                    <span className="el-rhythm-v"><bdi>{r.v} {p.classes.minutes}</bdi></span>
                    <span className="el-rhythm-l">{r.l}</span>
                  </li>
                ))}
              </ol>
            </div>
            <ol className="el-phases">
              {p.classes.phases.map((ph, i) => (
                <li key={i} className="el-phase reveal" style={{ "--d": `${i * 90}ms` }}>
                  <p className="el-phase-name"><span className="el-phase-n">{i + 1}</span>{ph.name}</p>
                  <ol className="el-phase-classes">
                    {ph.items.map((c) => (
                      <li key={c.n}>
                        <span className="el-class-n" aria-hidden="true">{String(c.n).padStart(2, "0")}</span>
                        <div>
                          <h3 className="el-class-t">{c.t}</h3>
                          <p className="el-class-b">{c.b}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ── What students learn and leave with ── */}
        <section className="el-section el-who" aria-label={p.outcomes.label}>
          <div className="el-inner">
            {label(p.outcomes.label)}
            <div className="el-twocol">
              <div className="el-col reveal">
                <h2 className="el-subhead">{p.outcomes.learnTitle}</h2>
                <div className="el-who-list el-who-list--single">
                  {p.outcomes.learn.map((w, i) => (
                    <div key={i} className="el-who-item"><span className="el-who-mark">{CHECK}</span>{w}</div>
                  ))}
                </div>
              </div>
              <div className="el-col reveal" style={{ "--d": "120ms" }}>
                <h2 className="el-subhead">{p.outcomes.leaveTitle}</h2>
                <div className="el-benefits el-benefits--single">
                  {p.outcomes.leave.map((o, i) => (
                    <div key={i} className="el-benefit">
                      <span className="el-benefit-check">{CHECK}</span>
                      <p className="el-benefit-text">{o}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Why Kidsology ── */}
        <section className="el-section" style={{ paddingTop: 0 }} aria-labelledby="whyk-title">
          <div className="el-inner">
            {label(p.whyKidsology.label)}
            <h2 id="whyk-title" className="el-visually-hidden">{p.whyKidsology.label}</h2>
            <div className="el-who-list el-who-list--single el-whyk reveal">
              {p.whyKidsology.items.map((w, i) => (
                <div key={i} className="el-who-item"><span className="el-who-mark">{CHECK}</span>{w}</div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Questions ── */}
        <section className="el-section" style={{ paddingTop: 0 }} aria-labelledby="faq-title">
          <div className="el-inner">
            {label(p.faq.label)}
            <h2 id="faq-title" className="el-visually-hidden">{p.faq.label}</h2>
            <ProgramFaq items={p.faq.items} />
          </div>
        </section>

        {/* ── The other programs ── */}
        <section className="el-section" style={{ paddingTop: 0 }} aria-labelledby="others-title">
          <div className="el-inner">
            <span className="el-label reveal" id="others-title">{ui.labelOther}</span>
            <ul className="el-program-others reveal">
              {others.map((o) => (
                <li key={o.id}>
                  <LocaleLink to={o.page} className="el-program-other" onClick={() => track("program_other_click", { from: slug, to: o.id })}>
                    <span className="el-program-other-eyebrow">{o.eyebrow}</span>
                    <span className="el-program-other-name">{o.name}</span>
                    {ARROW}
                  </LocaleLink>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>

      <section className="el-cta">
        <div className="el-cta-inner">
          <h2 className="el-cta-title el-display">{ui.ctaTitle}</h2>
          <p className="el-cta-sub">{ui.ctaSub}</p>
          <div className="el-cta-actions">
            <a href={lp("/#contact")} className="btn btn-gold-lg">{ui.register}</a>
            <LocaleLink to="/schools#enquire" className="btn btn-outline-white">{ui.schoolsCta}</LocaleLink>
          </div>
        </div>
      </section>
      </main>
    </div>
  );
}

/* One route component per spec program, for the router in src/App.js. */
export function programSpecRoute(slug) {
  function ProgramSpecRoute() {
    return <ProgramSpecPage slug={slug} />;
  }
  ProgramSpecRoute.displayName = `ProgramSpecPage(${slug})`;
  return ProgramSpecRoute;
}
